import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { securityApi, type MfaMethodSummary } from '../api/endpoints.ts';
import { ApiRequestError } from '../api/client.ts';
import { Alert, Button, LoadingBlock, TextInput } from './ui.tsx';
import ElevateDialog, { isFreshAuthRequired } from './elevate-dialog.tsx';
import PhoneSignIn from './phone-sign-in.tsx';
import { createCredential, wasDismissed, webauthnSupported } from '../lib/webauthn.ts';

/**
 * Settings → Security: the second factors on this account, the recovery codes
 * behind them, and where the account is signed in.
 *
 * Two things this screen is deliberately careful about, because both are
 * moments where somebody can lock themselves out:
 *
 *   * The enrollment secret and the recovery codes are shown ONCE. The screen
 *     says so, offers a copy, and keeps them in component state rather than
 *     re-fetching — because there is nothing to re-fetch.
 *   * The manual-entry secret is always offered next to the QR code. A QR code
 *     is unusable to somebody enrolling on the same phone that shows it, and
 *     unreadable to somebody using a screen reader.
 */
export default function SecuritySection() {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  const status = useQuery({ queryKey: ['security'], queryFn: () => securityApi.status() });
  const sessions = useQuery({ queryKey: ['security-sessions'], queryFn: () => securityApi.sessions() });
  const devices = useQuery({ queryKey: ['trusted-devices'], queryFn: () => securityApi.trustedDevices() });

  // Enrollment state, held here because the server will not hand any of it
  // back a second time.
  const [enrollment, setEnrollment] = useState<{ methodId: string; secret: string; uri: string } | null>(null);
  const [phone, setPhone] = useState('');
  const [smsChallenge, setSmsChallenge] = useState<{ challengeId: string; maskedPhone: string } | null>(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);

  /**
   * The action a freshness 403 interrupted, held so it can be resumed.
   *
   * Without this the person re-verifies and then has to remember what they
   * were doing and press it again — and the most common blocked action here is
   * removing a factor, where "did that work?" is exactly the wrong question to
   * leave somebody with.
   */
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  const failed = (caught: unknown, fallback: string, retry?: () => void) => {
    if (isFreshAuthRequired(caught)) {
      setError(null);
      setPendingAction(() => retry ?? null);
      return;
    }
    setError(caught instanceof ApiRequestError ? caught.message : fallback);
  };

  const startTotp = useMutation({
    mutationFn: () => securityApi.enrollTotp(),
    onSuccess: (result) => {
      setEnrollment(result);
      setError(null);
    },
    onError: (caught) => failed(caught, t('security.enrollError'), () => startTotp.mutate()),
  });

  const finishTotp = useMutation({
    mutationFn: () => securityApi.verifyTotp(enrollment?.methodId ?? '', code),
    onSuccess: (result) => {
      setEnrollment(null);
      setCode('');
      setError(null);
      if (result.recoveryCodes) setRecoveryCodes(result.recoveryCodes);
      void status.refetch();
    },
    onError: (caught) => failed(caught, t('security.codeError')),
  });

  const startSms = useMutation({
    mutationFn: () => securityApi.enrollSms(phone),
    onSuccess: (result) => {
      setSmsChallenge(result);
      setError(null);
    },
    onError: (caught) => failed(caught, t('security.enrollError'), () => startSms.mutate()),
  });

  const finishSms = useMutation({
    mutationFn: () => securityApi.verifySms(smsChallenge?.challengeId ?? '', code),
    onSuccess: (result) => {
      setSmsChallenge(null);
      setPhone('');
      setCode('');
      setError(null);
      if (result.recoveryCodes) setRecoveryCodes(result.recoveryCodes);
      void status.refetch();
    },
    onError: (caught) => failed(caught, t('security.codeError')),
  });

  /**
   * A security key, which is one round trip in the browser and two at the
   * server: options, then the attestation the authenticator produced.
   *
   * A dismissed prompt is not an error. Somebody who touched Escape, or who
   * had no key plugged in, does not need a red banner explaining what they
   * already know — `wasDismissed` is how the WebAuthn helper says so.
   */
  const addSecurityKey = useMutation({
    mutationFn: async () => {
      const options = await securityApi.securityKeyOptions();
      const created = await createCredential(options.publicKey);
      if (!created) return null;
      return securityApi.registerSecurityKey({
        challengeId: options.challengeId,
        label: t('security.securityKeyDefaultLabel'),
        clientDataJson: created.clientDataJson,
        attestationObject: created.attestationObject,
        ...(created.transports ? { transports: created.transports } : {}),
      });
    },
    onSuccess: (result) => {
      setError(null);
      if (result?.recoveryCodes) setRecoveryCodes(result.recoveryCodes);
      void status.refetch();
    },
    onError: (caught) => {
      if (wasDismissed(caught)) return;
      failed(caught, t('security.enrollError'), () => addSecurityKey.mutate());
    },
  });

  const removeMethod = useMutation({
    mutationFn: (id: string) => securityApi.remove(id),
    onSuccess: () => {
      setError(null);
      void status.refetch();
    },
    onError: (caught, id) => failed(caught, t('security.removeError'), () => removeMethod.mutate(id)),
  });

  const regenerate = useMutation({
    mutationFn: () => securityApi.regenerateRecoveryCodes(),
    onSuccess: (result) => {
      setRecoveryCodes(result.codes);
      setError(null);
      void status.refetch();
    },
    onError: (caught) => failed(caught, t('security.recoveryError'), () => regenerate.mutate()),
  });

  const signOutOthers = useMutation({
    mutationFn: () => securityApi.revokeOtherSessions(),
    onSuccess: () => void sessions.refetch(),
    onError: (caught) => failed(caught, t('security.sessionsError')),
  });

  const clearDevices = useMutation({
    mutationFn: () => securityApi.clearTrustedDevices(),
    onSuccess: () => void devices.refetch(),
    onError: (caught) => failed(caught, t('security.devicesError'), () => clearDevices.mutate()),
  });

  if (status.isLoading) return <LoadingBlock />;
  const data = status.data;
  if (!data) return null;

  return (
    <section className="space-y-4">
      <header>
        <h2 className="text-lg font-semibold">{t('security.title')}</h2>
        <p className="text-sm text-slate-600">{t('security.intro')}</p>
      </header>

      {error ? <Alert tone="error">{error}</Alert> : null}

      {/* The gate said "prove it's you first". This is the way through. */}
      {pendingAction !== null ? (
        <ElevateDialog
          onVerified={() => {
            const resume = pendingAction;
            setPendingAction(null);
            resume?.();
          }}
          onCancel={() => setPendingAction(null)}
        />
      ) : null}
      {data.required && data.methods.length === 0 ? <Alert tone="warning">{t('security.requiredNotice')}</Alert> : null}

      {/* The codes, shown once. */}
      {recoveryCodes ? (
        <div className="rounded border border-amber-300 bg-amber-50 p-4">
          <h3 className="font-semibold">{t('security.recoveryCodesTitle')}</h3>
          <p className="text-sm">{t('security.recoveryCodesOnce')}</p>
          <ul className="my-3 grid grid-cols-2 gap-1 font-mono text-sm">
            {recoveryCodes.map((recoveryCode) => (
              <li key={recoveryCode}>{recoveryCode}</li>
            ))}
          </ul>
          <Button
            type="button"
            variant="secondary"
            onClick={() => void navigator.clipboard?.writeText(recoveryCodes.join('\n'))}
          >
            {t('security.copyCodes')}
          </Button>{' '}
          <Button type="button" variant="ghost" onClick={() => setRecoveryCodes(null)}>
            {t('common.done')}
          </Button>
        </div>
      ) : null}

      <div className="rounded border border-slate-200 p-4">
        <h3 className="font-semibold">{t('security.methodsTitle')}</h3>
        {data.methods.length === 0 ? (
          <p className="mt-1 text-sm text-slate-600">{t('security.noMethods')}</p>
        ) : (
          <ul className="mt-2 divide-y divide-slate-100">
            {data.methods.map((method) => (
              <MethodRow
                key={method.id}
                method={method}
                onRemove={() => removeMethod.mutate(method.id)}
                removing={removeMethod.isPending}
              />
            ))}
          </ul>
        )}

        {enrollment ? (
          <div className="mt-4 space-y-3 rounded bg-slate-50 p-3">
            <p className="text-sm">{t('security.scanInstructions')}</p>
            {/* The URI itself, so a person can open it in their authenticator,
                and the secret in plain text — the accessible alternative to a
                QR code, and the only usable one when enrolling on the same
                phone that is displaying it. */}
            <p className="break-all font-mono text-xs">{enrollment.uri}</p>
            <p className="text-sm">
              {t('security.manualEntry')} <span className="font-mono">{enrollment.secret}</span>
            </p>
            <TextInput
              id="security-totp-code"
              label={t('security.enterCode')}
              value={code}
              onChange={(event) => setCode(event.target.value)}
              inputMode="numeric"
              autoComplete="one-time-code"
            />
            <Button type="button" onClick={() => finishTotp.mutate()} disabled={finishTotp.isPending}>
              {t('security.confirm')}
            </Button>{' '}
            <Button type="button" variant="ghost" onClick={() => setEnrollment(null)}>
              {t('common.cancel')}
            </Button>
          </div>
        ) : smsChallenge ? (
          <div className="mt-4 space-y-3 rounded bg-slate-50 p-3">
            <p className="text-sm">{t('security.smsSent', { phone: smsChallenge.maskedPhone })}</p>
            <TextInput
              id="security-sms-code"
              label={t('security.enterCode')}
              value={code}
              onChange={(event) => setCode(event.target.value)}
              inputMode="numeric"
              autoComplete="one-time-code"
            />
            <Button type="button" onClick={() => finishSms.mutate()} disabled={finishSms.isPending}>
              {t('security.confirm')}
            </Button>{' '}
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setSmsChallenge(null);
                setCode('');
              }}
            >
              {t('common.cancel')}
            </Button>
          </div>
        ) : (
          <div className="mt-3 space-y-3">
            {/* Every method the deployment can actually enroll, not just the
                first one that was built. `availableMethods` already accounts
                for both the policy and what this environment is configured to
                store, so a deployment with no authenticator key simply does
                not show that button — and one that offers nothing else would
                otherwise leave a mandated account with no way to comply. */}
            <div className="flex flex-wrap gap-2">
              {data.availableMethods.includes('totp') ? (
                <Button type="button" onClick={() => startTotp.mutate()} disabled={startTotp.isPending}>
                  {t('security.addAuthenticator')}
                </Button>
              ) : null}
              {data.availableMethods.includes('webauthn') && webauthnSupported() ? (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => addSecurityKey.mutate()}
                  disabled={addSecurityKey.isPending}
                >
                  {t('security.addSecurityKey')}
                </Button>
              ) : null}
            </div>

            {data.availableMethods.includes('sms') ? (
              <div className="flex flex-wrap items-end gap-2">
                <TextInput
                  id="security-sms-phone"
                  label={t('security.phoneLabel')}
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                  inputMode="tel"
                  autoComplete="tel"
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => startSms.mutate()}
                  disabled={startSms.isPending || phone.trim().length === 0}
                >
                  {t('security.addPhone')}
                </Button>
              </div>
            ) : null}
          </div>
        )}
      </div>

      <div className="rounded border border-slate-200 p-4">
        <h3 className="font-semibold">{t('security.recoveryTitle')}</h3>
        <p className="text-sm text-slate-600">
          {t('security.recoveryRemaining', { count: data.recoveryCodesRemaining })}
        </p>
        <Button
          type="button"
          variant="secondary"
          className="mt-2"
          onClick={() => regenerate.mutate()}
          disabled={regenerate.isPending || data.methods.length === 0}
        >
          {t('security.regenerate')}
        </Button>
      </div>

      <div className="rounded border border-slate-200 p-4">
        <h3 className="font-semibold">{t('security.devicesTitle')}</h3>
        <p className="text-sm text-slate-600">{t('security.devicesIntro', { days: data.trustedDeviceDays })}</p>
        <ul className="mt-2 text-sm">
          {(devices.data?.devices ?? []).map((device) => (
            <li key={device.id} className="py-1">
              {device.current ? t('security.thisDevice') : t('security.otherDevice')} ·{' '}
              {new Date(device.lastUsedAt).toLocaleDateString()}
            </li>
          ))}
        </ul>
        <Button type="button" variant="secondary" className="mt-2" onClick={() => clearDevices.mutate()}>
          {t('security.clearDevices')}
        </Button>
      </div>

      <PhoneSignIn
        onFreshAuthRequired={(retry) => {
          setError(null);
          setPendingAction(() => retry);
        }}
        onSignedIn={() => void sessions.refetch()}
      />

      <div className="rounded border border-slate-200 p-4">
        <h3 className="font-semibold">{t('security.sessionsTitle')}</h3>
        <ul className="mt-2 text-sm">
          {(sessions.data?.sessions ?? []).map((session) => (
            <li key={session.id} className="py-1">
              {session.current ? t('security.thisSession') : t('security.otherSession')} ·{' '}
              {new Date(session.lastUsedAt).toLocaleString()}
            </li>
          ))}
        </ul>
        <Button type="button" variant="secondary" className="mt-2" onClick={() => signOutOthers.mutate()}>
          {t('security.signOutOthers')}
        </Button>
      </div>
    </section>
  );
}

function MethodRow({
  method,
  onRemove,
  removing,
}: {
  method: MfaMethodSummary;
  onRemove: () => void;
  removing: boolean;
}) {
  const { t } = useTranslation();
  return (
    <li className="flex items-center justify-between py-2">
      <span className="text-sm">
        <span className="font-medium">{method.label}</span>
        {method.maskedPhone ? <span className="text-slate-600"> · {method.maskedPhone}</span> : null}
      </span>
      <Button type="button" variant="ghost" onClick={onRemove} disabled={removing}>
        {t('common.remove')}
      </Button>
    </li>
  );
}
